import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Building2,
  Image,
  FileText,
  Package,
  Users,
  Star,
  Target,
  Code,
  Settings,
  CreditCard,
  Plug,
  Activity,
  HelpCircle,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Bell,
  Search,
  Menu,
  X,
  User,
  ChevronDown,
  Shield,
  Share2,
  Radio,
  Award,
  Sparkles,
  BookOpen,
  Crown,
  Trophy,
  Video,
  BarChart3,
  MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const memberNavItems = [
  { id: "overview", label: "Overview", icon: LayoutDashboard, path: "/dashboard" },
  { id: "listing", label: "My Listing", icon: Building2, path: "/dashboard/listing" },
  { id: "media", label: "Media Library", icon: Image, path: "/dashboard/media" },
  { id: "content", label: "Content", icon: FileText, path: "/dashboard/content" },
  { id: "comments", label: "Comments", icon: MessageCircle, path: "/dashboard/comments" },
  { id: "inventory", label: "Inventory", icon: Package, path: "/dashboard/inventory" },
  { id: "saved-searches", label: "Saved Searches", icon: Bell, path: "/dashboard/saved-searches" },
  { id: "leads", label: "Leads", icon: Users, path: "/dashboard/leads" },
  { id: "analytics", label: "Analytics", icon: BarChart3, path: "/dashboard/analytics" },
  { id: "reviews", label: "Reviews", icon: Star, path: "/dashboard/reviews" },
  { id: "pixels", label: "Retargeting & Pixels", icon: Target, path: "/dashboard/pixels" },
  { id: "share", label: "Share & Promote", icon: Share2, path: "/dashboard/share" },
  { id: "referrals", label: "Referrals", icon: Award, path: "/dashboard/referrals" },
  { id: "onlybest", label: "OnlyBest Campaign", icon: Crown, path: "/dashboard/onlybest" },
  { id: "onlybest-stats", label: "OnlyBest Stats", icon: Trophy, path: "/dashboard/onlybest/stats" },
  { id: "live-events", label: "Live Events", icon: Video, path: "/dashboard/live-events" },
  { id: "livecasts", label: "LiveCast™", icon: Radio, path: "/dashboard/livecasts" },
  { id: "embeds", label: "Embeds", icon: Code, path: "/dashboard/embeds" },
  { id: "team", label: "Team Members", icon: Users, path: "/dashboard/team" },
  { id: "billing", label: "Billing", icon: CreditCard, path: "/dashboard/billing" },
  { id: "integrations", label: "Integrations", icon: Plug, path: "/dashboard/integrations" },
  { id: "activity", label: "Activity Log", icon: Activity, path: "/dashboard/activity" },
  { id: "settings", label: "Settings", icon: Settings, path: "/dashboard/settings" },
  { id: "knowledge-base", label: "Knowledge Base", icon: BookOpen, path: "/knowledge-base", external: true },
];

const adminNavItems = [
  { id: "admin-users", label: "Manage Users", icon: Users, path: "/dashboard/admin/users" },
  { id: "admin-claims", label: "Listing Claims", icon: Building2, path: "/dashboard/admin/claims" },
  { id: "admin-listings", label: "All Listings", icon: Award, path: "/dashboard/admin/listings" },
  { id: "admin-ghl", label: "GoHighLevel CRM", icon: Plug, path: "/dashboard/admin/ghl" },
  { id: "admin-recommendations", label: "Recommendations", icon: Sparkles, path: "/dashboard/admin/recommendations" },
  { id: "admin-api", label: "Admin API", icon: Shield, path: "/dashboard/admin/api" },
];

export default function DashboardLayout() {
  const { user, organization, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/auth/login");
  };

  const isActivePath = (path) => {
    if (path === "/dashboard") {
      return location.pathname === "/dashboard";
    }
    // Exact match for sub-routes that share a parent path prefix (e.g. /onlybest vs /onlybest/stats).
    if (path === "/dashboard/onlybest") {
      return location.pathname === "/dashboard/onlybest";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C]">
      {/* Top Bar */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-[#0F172A]/95 backdrop-blur-xl border-b border-white/10 z-50">
        <div className="flex items-center justify-between h-full px-4">
          {/* Left: Logo & Mobile Menu */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-white/5 text-white"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            
            <Link to="/dashboard" className="flex items-center" data-testid="dashboard-logo-link">
              <img
                src="/assets/localbest_transparent_v2.png"
                alt="LocalBest.ai"
                className="h-10 md:h-11 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Center: Search */}
          <div className="hidden md:flex flex-1 max-w-xl mx-8">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B]" />
              <input
                type="text"
                placeholder="Search listings, content, inventory..."
                className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#7C3AED]/50 text-sm"
              />
            </div>
          </div>

          {/* Right: Actions & Profile */}
          <div className="flex items-center gap-3">
            {isAdmin && (
              <Link to="/admin">
                <Button variant="ghost" size="sm" className="text-[#F59E0B] hover:bg-[#F59E0B]/10">
                  Admin Panel
                </Button>
              </Link>
            )}
            
            <button className="relative p-2 rounded-lg hover:bg-white/5 text-[#94A3B8]">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-[#EF4444] rounded-full" />
            </button>

            <button className="p-2 rounded-lg hover:bg-white/5 text-[#94A3B8]">
              <HelpCircle className="w-5 h-5" />
            </button>

            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#EC4899] flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.first_name?.[0] || user?.email?.[0]?.toUpperCase() || "U"}
                  </span>
                </div>
                <ChevronDown className={`w-4 h-4 text-[#94A3B8] transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-2 w-64 bg-[#1E293B] border border-white/10 rounded-xl shadow-2xl overflow-hidden"
                  >
                    <div className="p-4 border-b border-white/10">
                      <p className="text-white font-medium">{user?.first_name} {user?.last_name}</p>
                      <p className="text-sm text-[#64748B]">{user?.email}</p>
                      {organization && (
                        <p className="text-xs text-[#7C3AED] mt-1">{organization.name}</p>
                      )}
                    </div>
                    <div className="p-2">
                      <Link
                        to="/dashboard/settings"
                        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <User className="w-4 h-4" />
                        <span>Profile Settings</span>
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#EF4444]/10 text-[#EF4444] transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`fixed top-16 left-0 bottom-0 bg-[#0F172A]/95 backdrop-blur-xl border-r border-white/10 z-40 transition-all duration-300 ${
          sidebarCollapsed ? "w-20" : "w-64"
        } hidden lg:block`}
      >
        <div className="flex flex-col h-full">
          {/* Nav Items */}
          <nav className="flex-1 py-4 overflow-y-auto">
            <ul className="space-y-1 px-3">
              {memberNavItems.map((item) => (
                <li key={item.id}>
                  <Link
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                      isActivePath(item.path)
                        ? "bg-[#7C3AED]/20 text-white border-l-2 border-[#7C3AED]"
                        : "text-[#94A3B8] hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    <item.icon className={`w-5 h-5 flex-shrink-0 ${isActivePath(item.path) ? "text-[#7C3AED]" : ""}`} />
                    {!sidebarCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                  </Link>
                </li>
              ))}
            </ul>
            {/* Admin Section */}
            {isAdmin && (
              <div className="mt-4 pt-4 border-t border-white/10">
                {!sidebarCollapsed && (
                  <p className="px-6 mb-2 text-xs text-[#F59E0B] uppercase tracking-wider font-semibold">Admin</p>
                )}
                <ul className="space-y-1 px-3">
                  {adminNavItems.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                          isActivePath(item.path)
                            ? "bg-[#F59E0B]/20 text-white border-l-2 border-[#F59E0B]"
                            : "text-[#94A3B8] hover:bg-white/5 hover:text-white"
                        }`}
                      >
                        <item.icon className={`w-5 h-5 flex-shrink-0 ${isActivePath(item.path) ? "text-[#F59E0B]" : ""}`} />
                        {!sidebarCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </nav>

          {/* Collapse Toggle */}
          <div className="p-3 border-t border-white/10">
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 text-[#64748B] hover:text-white transition-colors"
            >
              {sidebarCollapsed ? (
                <ChevronRight className="w-5 h-5" />
              ) : (
                <>
                  <ChevronLeft className="w-5 h-5" />
                  <span className="text-sm">Collapse</span>
                </>
              )}
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setMobileMenuOpen(false)}
            />
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              className="fixed top-16 left-0 bottom-0 w-72 bg-[#0F172A] border-r border-white/10 z-50 lg:hidden overflow-y-auto"
            >
              <nav className="py-4">
                <ul className="space-y-1 px-3">
                  {memberNavItems.map((item) => (
                    <li key={item.id}>
                      <Link
                        to={item.path}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                          isActivePath(item.path)
                            ? "bg-[#7C3AED]/20 text-white"
                            : "text-[#94A3B8] hover:bg-white/5 hover:text-white"
                        }`}
                      >
                        <item.icon className={`w-5 h-5 ${isActivePath(item.path) ? "text-[#7C3AED]" : ""}`} />
                        <span className="text-sm font-medium">{item.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
                {isAdmin && (
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="px-6 mb-2 text-xs text-[#F59E0B] uppercase tracking-wider font-semibold">Admin</p>
                    <ul className="space-y-1 px-3">
                      {adminNavItems.map((item) => (
                        <li key={item.id}>
                          <Link
                            to={item.path}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                              isActivePath(item.path)
                                ? "bg-[#F59E0B]/20 text-white"
                                : "text-[#94A3B8] hover:bg-white/5 hover:text-white"
                            }`}
                          >
                            <item.icon className={`w-5 h-5 ${isActivePath(item.path) ? "text-[#F59E0B]" : ""}`} />
                            <span className="text-sm font-medium">{item.label}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main
        className={`pt-16 min-h-screen transition-all duration-300 ${
          sidebarCollapsed ? "lg:pl-20" : "lg:pl-64"
        }`}
      >
        <div className="p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
